import React, { useState, useEffect } from "react";
import { getAllDecisions, getAISentiment } from "../lib/api";
import { Loader2, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp, Brain, ArrowUpDown, ArrowUp, ArrowDown, HelpCircle } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const ACTION_STYLES = {
  "Buy": "bg-green-500/15 text-green-400 border-green-500/30",
  "Add": "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  "Hold": "bg-blue-500/15 text-blue-400 border-blue-500/30",
  "Reduce": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "Exit": "bg-red-500/20 text-red-400 border-red-500/30",
  "Review": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

export default function DecisionsPage({ onStockClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [sentimentLoading, setSentimentLoading] = useState(null);
  const [sortKey, setSortKey] = useState("total_score");
  const [sortDir, setSortDir] = useState("desc");
  const [filterAction, setFilterAction] = useState("all");

  useEffect(() => {
    getAllDecisions().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSentiment = async (symbol) => {
    setSentimentLoading(symbol);
    try {
      const r = await getAISentiment(symbol);
      // Update the decision with sentiment data
      setData(prev => {
        if (!prev) return prev;
        const decisions = prev.decisions.map(d =>
          d.symbol === symbol ? { ...d, ai_sentiment: r.data } : d
        );
        return { ...prev, decisions };
      });
    } catch (err) { console.error(err); }
    finally { setSentimentLoading(null); }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[var(--brand)]" size={32} /></div>;

  const decisions = data?.decisions || [];
  const summary = data?.summary || {};
  const actionCounts = summary.action_counts || {};

  const sorted = [...decisions]
    .filter(d => filterAction === "all" || d.action === filterAction)
    .sort((a, b) => {
      const av = a[sortKey] ?? 0, bv = b[sortKey] ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="decisions-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>Stock Recommendations</h1>
        <p className="text-sm text-[var(--text-muted)]">
          One evidence-based view of what to add, hold, reduce or exit. Confidence measures data coverage—not conviction.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="flex flex-wrap gap-2" data-testid="action-filter">
        <ActionPill label="All" count={decisions.length} active={filterAction === "all"} onClick={() => setFilterAction("all")} color="bg-white/10 text-white" />
        {["Buy", "Add", "Hold", "Reduce", "Exit", "Review"].map(a => (
          <ActionPill key={a} label={a} count={actionCounts[a] || 0} active={filterAction === a} onClick={() => setFilterAction(a)}
            color={ACTION_STYLES[a]} />
        ))}
      </div>

      {/* Decisions Table */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden" data-testid="decisions-table">
        {sorted.length === 0 ? (
          <div className="text-center py-12 text-sm text-[var(--text-muted)]">
            No stocks with "{filterAction}" recommendation.
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold px-4 py-3">Stock</th>
                <th className="text-left text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold px-2 py-3">Account</th>
                <SortTh label="Score" k="total_score" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <th className="text-center text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold px-2 py-3">Action</th>
                <SortTh label="Value" k="current_value" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortTh label="P&L %" k="pnl_pct" cur={sortKey} dir={sortDir} onClick={toggleSort} />
                <th className="text-center text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold px-2 py-3">RSI</th>
                <th className="text-center text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold px-2 py-3">Trend</th>
                <th className="text-right text-xs uppercase tracking-wider text-[var(--text-muted)] font-semibold px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((d) => (
                <React.Fragment key={`${d.symbol}-${d.broker}`}>
                  <tr className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => onStockClick ? onStockClick(d.symbol) : setExpanded(expanded === d.symbol ? null : d.symbol)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium text-white">{d.symbol}</span>
                        <span className="text-[10px] text-[var(--text-muted)]">{d.sector}</span>
                      </div>
                      <div className="mt-1 text-[10px] text-[var(--text-muted)]">
                        {d.compounder || "Insufficient long-term evidence"}
                      </div>
                    </td>
                    <td className="px-2 py-3 text-xs text-[var(--text-muted)]">{d.account_name || d.broker}</td>
                    <td className="px-2 py-3 text-center">
                      <ScoreBadge score={d.total_score} />
                    </td>
                    <td className="px-2 py-3 text-center">
                      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Badge variant="outline" className={`text-xs font-semibold border ${ACTION_STYLES[d.action] || ACTION_STYLES.Hold}`} data-testid={`action-${d.symbol}`}>
                          {d.action}
                        </Badge>
                        <WhyActionPopover decision={d} />
                      </div>
                      <div className="mt-1">
                        <ConfidenceBadge confidence={d.confidence} />
                      </div>
                    </td>
                    <td className="px-2 py-3 text-right font-mono text-sm text-[var(--text-secondary)]">{fmtINR(d.current_value)}</td>
                    <td className="px-2 py-3 text-right">
                      <span className={`font-mono text-sm ${d.pnl_pct >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                        {d.pnl_pct >= 0 ? "+" : ""}{(d.pnl_pct || 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-2 py-3 text-center">
                      <RSIBadge rsi={d.signals?.rsi} />
                    </td>
                    <td className="px-2 py-3 text-center">
                      <TrendBadge trend={d.signals?.trend} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {expanded === d.symbol ? <ChevronUp size={14} className="text-[var(--text-muted)]" /> : <ChevronDown size={14} className="text-[var(--text-muted)]" />}
                    </td>
                  </tr>
                  {expanded === d.symbol && d.breakdown && (
                    <tr>
                      <td colSpan={9} className="bg-[var(--bg-base)] px-6 py-4 border-b border-white/5">
                        <BreakdownPanel decision={d} onSentiment={handleSentiment} sentimentLoading={sentimentLoading} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
}

function BreakdownPanel({ decision, onSentiment, sentimentLoading }) {
  const b = decision.breakdown;
  if (!b) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
      {["quality", "valuation", "momentum", "risk", "portfolio_fit"].map(key => {
        const d = b[key];
        if (!d) return null;
        const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        const pct = (d.score / d.max) * 100;
        return (
          <div key={key} className="bg-[var(--bg-surface)] border border-white/5 rounded p-3">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">{label}</span>
              <span className="font-mono text-sm text-white">{d.score}/{d.max}</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 mb-2">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct > 70 ? "var(--positive)" : pct > 40 ? "var(--warning)" : "var(--negative)" }} />
            </div>
            {d.reasons?.map((r, i) => (
              <div key={i} className="text-[11px] text-[var(--text-muted)] leading-relaxed">- {r}</div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function ActionPill({ label, count, active, onClick, color }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${active ? color + " border-current" : "bg-transparent border-white/10 text-[var(--text-muted)] hover:text-white"}`}>
      {label} {count > 0 && <span className="font-mono ml-1">({count})</span>}
    </button>
  );
}

function ScoreBadge({ score }) {
  if (score == null) return <span className="text-xs text-[var(--text-muted)]">Not scored</span>;
  const color = score >= 80 ? "text-emerald-400" : score >= 65 ? "text-green-400" : score >= 50 ? "text-blue-400" : score >= 35 ? "text-amber-400" : "text-red-400";
  return <span className={`font-mono text-sm font-semibold ${color}`}>{score}</span>;
}

function ConfidenceBadge({ confidence }) {
  const label = confidence?.label || "Low";
  const score = confidence?.score ?? 0;
  const color = label === "High" ? "text-emerald-300" : label === "Medium" ? "text-amber-300" : "text-slate-400";
  return <span className={`text-[9px] font-semibold uppercase tracking-wider ${color}`}>{label} confidence · {score}%</span>;
}

function RSIBadge({ rsi }) {
  if (!rsi) return <span className="text-xs text-[var(--text-muted)]">--</span>;
  const color = rsi < 30 ? "text-emerald-400" : rsi > 70 ? "text-red-400" : "text-[var(--text-secondary)]";
  return <span className={`font-mono text-xs ${color}`}>{rsi.toFixed(0)}</span>;
}

function TrendBadge({ trend }) {
  if (!trend || trend === "neutral") return <Minus size={14} className="text-[var(--text-muted)] mx-auto" />;
  if (trend === "bullish") return <TrendingUp size={14} className="text-[var(--positive)] mx-auto" />;
  return <TrendingDown size={14} className="text-[var(--negative)] mx-auto" />;
}

function SortTh({ label, k, cur, dir, onClick }) {
  const active = cur === k;
  return (
    <th className="text-right text-xs uppercase tracking-wider font-semibold px-2 py-3 cursor-pointer select-none group" onClick={() => onClick(k)}>
      <span className={`inline-flex items-center gap-1 ${active ? "text-[var(--brand)]" : "text-[var(--text-muted)] group-hover:text-[var(--text-secondary)]"}`}>
        {label}
        {active ? (dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : <ArrowUpDown size={11} className="opacity-40" />}
      </span>
    </th>
  );
}

function fmtINR(v) {
  if (!v) return "--";
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

const _DIM_LABELS = {
  quality: { name: "Business Quality", max: 30, color: "text-violet-400", icon: "■" },
  valuation: { name: "Valuation", max: 15, color: "text-cyan-400", icon: "◆" },
  momentum: { name: "Momentum", max: 20, color: "text-blue-400", icon: "▲" },
  technical: { name: "Technical", max: 30, color: "text-cyan-400", icon: "▲" },
  fundamental: { name: "Fundamental", max: 25, color: "text-violet-400", icon: "■" },
  portfolio_fit: { name: "Portfolio Fit", max: 20, color: "text-blue-400", icon: "◆" },
  sentiment: { name: "Sentiment", max: 10, color: "text-amber-400", icon: "✦" },
  risk: { name: "Risk", max: 15, color: "text-rose-400", icon: "⚠" },
  // Fundamental-cache fallback uses these 5 dimensions instead:
  value: { name: "Value", max: 10, color: "text-cyan-400", icon: "▲" },
  future: { name: "Future Growth", max: 10, color: "text-violet-400", icon: "▲" },
  past: { name: "Past Performance", max: 10, color: "text-blue-400", icon: "▲" },
  health: { name: "Financial Health", max: 10, color: "text-amber-400", icon: "▲" },
  dividend: { name: "Dividend", max: 10, color: "text-rose-400", icon: "▲" },
};

function WhyActionPopover({ decision }) {
  const b = decision.breakdown;
  const total = decision.total_score;
  const sourceLabel = decision.source === "unified_fundamental_only"
    ? "Fundamentals only"
    : decision.source === "insufficient_data"
    ? "Insufficient evidence"
    : "Unified evidence model";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="text-[var(--text-muted)] hover:text-white p-0.5 rounded transition-colors"
          data-testid={`why-${decision.symbol}`}
          aria-label={`Why ${decision.action}?`}
        >
          <HelpCircle size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="center"
        className="bg-[var(--bg-base)] border-white/10 text-white p-4 w-[340px] shadow-2xl"
        data-testid={`why-popover-${decision.symbol}`}
      >
        <div className="flex items-start justify-between mb-3 pb-2 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Why {decision.action}?</div>
            <div className="font-mono text-xl font-bold text-white mt-0.5">
              {total == null ? "Not scored" : <>{total}<span className="text-sm text-[var(--text-muted)]">/100</span></>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)] font-semibold">Source</div>
            <div className="text-[10px] text-[var(--text-secondary)]">{sourceLabel}</div>
          </div>
        </div>

        {b ? (
          <div className="space-y-2">
            {Object.keys(_DIM_LABELS)
              .filter(k => b[k])
              .map(k => {
                const dim = b[k];
                const meta = _DIM_LABELS[k];
                const pct = (dim.score / dim.max) * 100;
                return (
                  <div key={k} className="text-[11px]">
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`font-semibold uppercase tracking-wider text-[10px] ${meta.color}`}>
                        {meta.icon} {meta.name}
                      </span>
                      <span className="font-mono text-white">
                        {dim.score}<span className="text-[var(--text-muted)]">/{dim.max}</span>
                      </span>
                    </div>
                    <div className="h-1 rounded-full bg-white/5 mb-0.5">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: pct > 70 ? "var(--positive)" : pct > 40 ? "var(--warning)" : "var(--negative)",
                        }}
                      />
                    </div>
                    {dim.reasons?.slice(0, 2).map((r, i) => (
                      <div key={i} className="text-[10px] text-[var(--text-muted)] leading-snug pl-3">
                        — {r}
                      </div>
                    ))}
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="text-[11px] text-[var(--text-muted)] italic py-2">
            No detailed breakdown available — this stock falls back to a neutral default
            because both live signals and fundamental scoring failed.
          </div>
        )}

        {decision.signals && (
          <div className="mt-3 pt-2 border-t border-white/10 grid grid-cols-3 gap-1.5 text-[10px]">
            {decision.signals.rsi != null && (
              <div className="bg-white/5 rounded px-1.5 py-1">
                <div className="text-[var(--text-muted)] uppercase tracking-wider text-[9px]">RSI</div>
                <div className="font-mono text-white">{decision.signals.rsi.toFixed(0)}</div>
              </div>
            )}
            {decision.signals.trend && (
              <div className="bg-white/5 rounded px-1.5 py-1">
                <div className="text-[var(--text-muted)] uppercase tracking-wider text-[9px]">Trend</div>
                <div className="text-white capitalize">{decision.signals.trend}</div>
              </div>
            )}
            {decision.signals.pct_from_52w_high != null && (
              <div className="bg-white/5 rounded px-1.5 py-1">
                <div className="text-[var(--text-muted)] uppercase tracking-wider text-[9px]">vs 52w</div>
                <div className="font-mono text-white">{decision.signals.pct_from_52w_high.toFixed(0)}%</div>
              </div>
            )}
          </div>
        )}
        <div className="mt-3 pt-2 border-t border-white/10">
          <ConfidenceBadge confidence={decision.confidence} />
          {decision.confidence?.missing?.length > 0 && (
            <div className="mt-1 text-[10px] text-amber-300">
              Missing: {decision.confidence.missing.join(", ")}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
